import { useMemo } from 'react';

import type {
	CommentData,
	ReplyData,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type { CommentInlineLocation as CommentLocation } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { useCommentsPanelState } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import { useProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { expValEquals } from '@confluence/feature-experiments';

const createInlineMarkerMatcher =
	(parentLocation: CommentLocation) =>
	(reply: ReplyData | null): boolean => {
		if (!reply) {
			return false;
		}

		const replyLocation = reply.location as CommentLocation;

		return parentLocation.inlineMarkerRef === replyLocation.inlineMarkerRef;
	};

export const useIsEditorExpanded = (
	parentComment: CommentData,
	location: LocationValue,
): boolean => {
	const { hasNestedComments } = useProcessedCommentsDataState();

	const { replyToCommentId, location: commentLocation } = useCommentsPanelState();

	return useMemo(() => {
		if (location === LocationValue.FOOTER) {
			if (commentLocation === LocationValue.FOOTER) {
				return (
					parentComment.id === replyToCommentId ||
					// Editor management depends on reply nesting configuration:
					// - With nested replies: ObjectCommentNestableReplies handles the Editor
					// - Without nested replies: Open Editor when replyToComment query parameter
					//   matches a reply's ID
					(!hasNestedComments &&
						parentComment.replies.some((reply) => reply?.id === replyToCommentId))
				);
			}

			return false;
		}

		const inlineMarkerMatcher = createInlineMarkerMatcher(
			parentComment.location as CommentLocation,
		);

		const parentReplies = parentComment.replies || [];
		const matchedReplies: ReplyData[] = parentReplies.filter(inlineMarkerMatcher);

		if (commentLocation === LocationValue.PANEL) {
			const isReplyingToParent = replyToCommentId === parentComment.id;
			const isReplyingToChild = matchedReplies.some((reply) => reply?.id === replyToCommentId);

			const canExpandForChildReply =
				isReplyingToChild &&
				expValEquals('confluence_comment_expand_reply_editor', 'isEnabled', true);

			return isReplyingToParent || canExpandForChildReply;
		}

		return false;
	}, [
		commentLocation,
		hasNestedComments,
		location,
		parentComment.id,
		parentComment.location,
		parentComment.replies,
		replyToCommentId,
	]);
};
